import React, { useState, useEffect } from "react";
import styles from "./ItemExchange.module.css";
import BackButton from "../../BackButton/BackButton";
import ExchangeFeedbackForm from "./ExchangeFeedbackForm";
import SelectNewItem from "./SelectNewItem";

//users step through each item being returned and select a new replacement item.
const ItemExchange = ( { business, exchangeProducts, setRenderItemExchange, selectedProducts, flattenedProducts, setFlattenedProducts, setRenderExchangeItemSelect, currentIndex, setCurrentIndex, exchangeProductData, newItemsSelectedOptions, setNewItemsSelectedOptions, setRenderExchangeConfirmation, feedbackChoicesByProduct } ) => {
	const [choices, setChoices] = useState(null);
	const [selectedReason, setSelectedReason] = useState(null); //primary key of reason on feedback form. Needs to be reset to null whenever user toggles back and forth
	const [allExchangeReasons, setAllExchangeReasons] = useState({}); //all choices configured on the default form
	const [loading, setLoading] = useState(false);
	const [progressBar, setProgressBar] = useState(currentIndex+1); //currentIndex is 0 indexed. Initial state should have the bar cover progressBarStepSize amount of width 
	const [renderExchangeFeedbackForm, setRenderExchangeFeedbackForm] = useState(true);
	const [renderSelectNewItem, setRenderSelectNewItem] = useState(false);
	const [showItemExchangeValidatorPopUp, setShowItemExchangeValidatorPopUp] = useState(false);
	const [validationMessages, setValidationMessages] = useState([]);
	const currentProductBeingExchanged = flattenedProducts.find(product => product.unique_id === selectedProducts[currentIndex]);
	var showExchangeFeedbackFormWithExchanges = false;
	try { showExchangeFeedbackFormWithExchanges = exchangeProductData.find(product => product.product_title === currentProductBeingExchanged.product_title).show_with_exchanges; } //this will automatically be false if no feedback options are configured
	catch (err) { console.log("return item does not exist in exchange product config!"); }
	const progressBarStepSize = 100 / selectedProducts.length; 

	//fetch feedback choices for first item shown
	useEffect(() => {
		if (currentIndex !== null) { 
			setProgressBar((currentIndex+1) * progressBarStepSize);
			//get product id using current index
			const uniqueId = selectedProducts[currentIndex];
			const productId = flattenedProducts.find(p => p.unique_id === uniqueId)?.product_id;
			const feedbackObj = feedbackChoicesByProduct[productId]
			if (Object.keys(feedbackObj).length === 0) { 
				setRenderExchangeFeedbackForm(false);
				setRenderSelectNewItem(true);
			}
			setChoices(feedbackObj);
		}
	}, [currentIndex]);

	useEffect(() => {
		if (newItemsSelectedOptions.length !== 0 && exchangeProductData) { validateItem(); }
	}, [newItemsSelectedOptions, currentIndex]);

	//next item
	const handleNext = () => {
		setSelectedReason(null);
		if (validationMessages.length > 0) { 
			setShowItemExchangeValidatorPopUp(true); 
			return;
		}
		if (currentIndex < selectedProducts.length - 1) { 
			setCurrentIndex(currentIndex + 1); 
			setRenderSelectNewItem(false);
			setRenderExchangeFeedbackForm(true);
		} else {
			setRenderExchangeConfirmation(true);
			setRenderItemExchange(false);	
		}
	};

	//previous item
	const handleBack = () => {
		setSelectedReason(null);
		if (flattenedProducts.length === 1 && currentIndex === 0) { return; } //disable if on first (0th) index and there is only one product available to exchange
		setRenderSelectNewItem(false);
		setRenderExchangeFeedbackForm(true);
		if (currentIndex > 0) { setCurrentIndex(currentIndex - 1); }
		else { 
			setRenderItemExchange(false); 
			setRenderExchangeItemSelect(true); 
		}
	};

	//validator for continuing to ExchangeConfirmation. Each selected product must have a non null value for all required variants
	const validateItem = () => {
		const missingVariants = [];		
		const selectedEntry = newItemsSelectedOptions.find(product => product.unique_id === currentProductBeingExchanged.unique_id);
		if (!("selected_product" in selectedEntry)) { missingVariants.push(`No product selected!`); }
		else {
			const productDef = exchangeProductData.find(p => p.product_title === selectedEntry.selected_product && p.selected); // Match the product definition by product_title (or unique_id if available in both places)
			const requiredKeys = productDef.shopify_options.filter(opt => opt.values.length > 1).map(opt => opt.name);
			const missingKeys = requiredKeys.filter(key => !(key in selectedEntry) || selectedEntry[key] === null || selectedEntry[key] === undefined);
			if (missingKeys.length > 0) {
				let formattedKeys = "";
				if (missingKeys.length === 1) { formattedKeys = missingKeys[0]; }
				else if (missingKeys.length === 2) { formattedKeys = `${missingKeys[0]} and ${missingKeys[1]}`; }
				else { formattedKeys = `${missingKeys.slice(0, -1).join(", ")}, and ${missingKeys.slice(-1)}`; }
				missingVariants.push(`No ${formattedKeys.toLowerCase()} selected!`);
			}
		}
		if (missingVariants.length > 0) {
			setValidationMessages(missingVariants);
			return;
		}
		setValidationMessages([]);
	};

	return (
		<div className={`${styles.item_exchange_container} ${selectedProducts.length == 1 ? styles.shrink : ""}`}>
			{showItemExchangeValidatorPopUp && (
				<div className={`${styles.validation_box} ${styles.wobble}`}>
					<img src={`${process.env.PUBLIC_URL}/images/remove_popup.png`} alt={"remove_item_exchange_validator"} className={`${styles.remove_item_exchange_validator}`} onClick={() => { setShowItemExchangeValidatorPopUp(false); } } />
					<p>Missing selections for your item{validationMessages.length > 1 ? 's' : ''}!</p>
					<div className={styles.validation_messages}>
						{validationMessages.map((msg, index) => (<p key={index}>{msg}</p>))}
					</div>
				</div>
			)}
			<p className={`titles ${styles.item_exchange_title} fade_in`}>Choose new items.</p>
			<div className={`${styles.new_item_selection_box} fade_in`}>
				<p className={styles.item_being_exchanged_text}>Item you will send back</p>
				{selectedProducts.length > 0 && currentIndex !== null && (
					<div className={styles.items_being_exchanged_container} key={currentIndex}>
						<img src={currentProductBeingExchanged.product_image} alt={"product_image"} className={`${styles.product_images} fade_in`} />
						<div className={styles.product_being_exchanged_title_variant_wrapper}>
							<p className={`fade_in`}>{currentProductBeingExchanged.product_title} / {currentProductBeingExchanged.product_variant}</p>
						</div>
					</div>
				)}
				{loading && (<p className={styles.item_being_exchanged_text}>loading..</p>)}
				{!loading && (<p className={styles.item_being_exchanged_text}>{renderExchangeFeedbackForm ? "Reason for exchange" : "Select new item."}</p>)}
				<div className={styles.new_item_selection_container}>
					{loading && (<img className={styles.item_exchange_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
					{!loading && renderExchangeFeedbackForm && showExchangeFeedbackFormWithExchanges && (<ExchangeFeedbackForm choices={choices} setRenderExchangeFeedbackForm={setRenderExchangeFeedbackForm} setRenderSelectNewItem={setRenderSelectNewItem} currentIndex={currentIndex} selectedProducts={selectedProducts} flattenedProducts={flattenedProducts} setFlattenedProducts={setFlattenedProducts} selectedReason={selectedReason} setSelectedReason={setSelectedReason} />)}
				{!loading && (renderSelectNewItem || !showExchangeFeedbackFormWithExchanges) && (<SelectNewItem businessKey={business} currentProductBeingExchanged={currentProductBeingExchanged} exchangeProductData={exchangeProductData} newItemsSelectedOptions={newItemsSelectedOptions} setNewItemsSelectedOptions={setNewItemsSelectedOptions} />)}	
				</div>
				{selectedProducts.length !== 1 && (
					<div className={styles.progress_bar_container}>
						<div className={styles.progress_bar} style={{ width: `${progressBar}%` }}></div>
					</div>
				)}
			</div>
			<div className={styles.back_forward_button_container}>
				<BackButton button_id={`${styles.item_exchange_back_button} ${flattenedProducts.length === 1 && currentIndex === 0 ? styles.hide : ""}`} image_id={styles.item_exchange_back_button_arrow} customBack={handleBack} />
				<BackButton button_id={`${styles.item_exchange_forward_button} ${validationMessages.length > 0 ? styles.dimmed : ""} ${flattenedProducts.length === 1 && currentIndex === 0 ? styles.shift : ""}`} image_id={styles.item_exchange_forward_button_arrow} customBack={handleNext} />
			</div>
		</div>
	);
};

export default ItemExchange;